import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Home as HomeIcon,
  Movie as MovieIcon,
  Person as PersonIcon,
  Event as EventIcon,
  History as HistoryIcon,
  ChevronLeft,
  ChevronRight,
  Psychology as AgentIcon,
  Business as BusinessIcon,
  CardMembership as SubscriptionIcon,
  TrendingUp as TrendingUpIcon,
  CompareArrows as CompareArrowsIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { brandColors } from '../../theme/theme';
import { hasAnyAgentAccess, hasAgentAccess, AGENT_ROLES } from '../../lib/agentRoles';
import { useFilmSubscription } from '../../hooks/useFilmSubscription';

const drawerWidth = 280;
const collapsedDrawerWidth = 64;

const Sidebar = ({ open, onToggle, isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isFilmEnterpriseAdmin, isFilmAdmin, userProfile } = useAuth();
  const { hasFilmTier } = useFilmSubscription();
  
  // Check if user has admin privileges
  const hasAdminAccess = isAdmin();
  
  // Check if user is enterprise admin
  const hasEnterpriseAdminAccess = isFilmEnterpriseAdmin();
  
  // Check if user is film admin
  const hasFilmAdminAccess = isFilmAdmin();
  
  // Check if user has any agent access
  const showAgentsSection = hasAnyAgentAccess(userProfile);
  
  const menuItems = [
    {
      text: 'Home',
      icon: <HomeIcon />,
      path: '/',
      group: 'home'
    }
  ];

  const filmTrackingItems = [
    {
      text: 'Film Dashboard',
      icon: <MovieIcon />,
      path: '/film-tracking'
    },
    {
      text: 'Film Deep Dive',
      icon: <InsightsIcon />,
      path: '/film-tracking/deep-dive'
    },
    {
      text: 'Legacy Quorum',
      icon: <HistoryIcon />,
      path: '/film-tracking/legacy-quorum'
    }
  ];

  const agentItems = [
    {
      text: 'Logline Analysis',
      icon: <AgentIcon />,
      path: '/agents/logline-analysis',
      requiresRole: AGENT_ROLES.LOGLINE_ANALYSIS
    }
    // Future agents will be added here
  ];

  const utilityItems = [
    {
      text: 'Release Calendar',
      icon: <EventIcon />,
      path: '/release-calendar'
    }
  ];

  const adminItems = [
    {
      text: 'User Management',
      icon: <PersonIcon />,
      path: '/admin/users'
    },
    {
      text: 'Film Subscriptions',
      icon: <SubscriptionIcon />,
      path: '/admin/film-subscriptions'
    },
    {
      text: 'Enterprise Companies',
      icon: <BusinessIcon />,
      path: '/admin/enterprise-companies'
    }
  ];

  const enterpriseAdminItems = [
    {
      text: 'Enterprise Admin',
      icon: <BusinessIcon />,
      path: '/enterprise/admin'
    },
    {
      text: 'Enterprise Dashboard',
      icon: <BusinessIcon />,
      path: '/enterprise/company/dashboard'
    },
    {
      text: 'Enterprise Users',
      icon: <PersonIcon />,
      path: '/enterprise/company/users'
    }
  ];

  const filmAdminItems = [
    {
      text: 'Box Office Projections',
      icon: <TrendingUpIcon />,
      path: '/film-admin/box-office-projections'
    },
    {
      text: 'Film Comps',
      icon: <CompareArrowsIcon />,
      path: '/film-admin/comps'
    }
  ];

  const handleItemClick = (path) => {
    navigate(path);
    // Close mobile menu when item is clicked
    if (isMobile) {
      onToggle();
    }
  };



  const isActive = (path) => {
    return location.pathname === path;
  };

  const renderMenuItem = (item, index) => {
    // Check if item requires a specific role
    if (item.requiresRole && !hasAgentAccess(userProfile, item.requiresRole)) {
      return null; // Don't render if user doesn't have required role
    }
    
    let disabled = false;
    let secondaryText = null;
    if (item.requiresTier && !hasFilmTier(item.requiresTier)) {
      disabled = true;
      secondaryText = 'Upgrade to Film Professional';
    }

    return (
      <ListItem key={index} disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          onClick={() => handleItemClick(item.path)}
          disabled={disabled}
          sx={{
            minHeight: 48,
            justifyContent: open ? 'initial' : 'center',
            px: 2.5,
            backgroundColor: isActive(item.path) ? 'rgba(255, 68, 73, 0.1)' : 'transparent',
            borderRight: isActive(item.path) ? `3px solid ${brandColors.secondary}` : 'none',
            opacity: disabled ? 0.6 : 1,
            '&:hover': {
              backgroundColor: disabled ? 'rgba(1, 39, 59, 0.04)' : 'rgba(1, 39, 59, 0.08)'
            }
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 3 : 'auto',
              justifyContent: 'center',
              color: isActive(item.path) ? brandColors.secondary : brandColors.primary
            }}
          >
            {item.icon}
          </ListItemIcon>
          {open && (
            <ListItemText 
              primary={item.text}
              secondary={disabled ? secondaryText : null}
              sx={{
                color: isActive(item.path) ? brandColors.secondary : brandColors.primary,
                fontWeight: isActive(item.path) ? 600 : 400
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const renderAgentsSection = () => {
    if (!showAgentsSection) return null;

    return (
      <>
        <Divider sx={{ my: 2, mx: 2 }} />
        <Box sx={{ px: 2, py: 1 }}>
          {open && (
            <Typography
              variant="overline"
              sx={{
                color: brandColors.secondary,
                fontWeight: 600,
                fontSize: '0.75rem',
                letterSpacing: '1px'
              }}
            >
              AGENTS
            </Typography>
          )}
        </Box>
        <List>
          {agentItems.map((item, index) => renderMenuItem(item, `agent-${index}`))}
        </List>
      </>
    );
  };

  const renderAdminSection = () => {
    if (!hasAdminAccess) return null;

    return (
      <>
        <Divider sx={{ my: 2, mx: 2 }} />
        <Box sx={{ px: 2, py: 1 }}>
          {open && (
            <Typography
              variant="overline"
              sx={{
                color: brandColors.secondary,
                fontWeight: 600,
                fontSize: '0.75rem',
                letterSpacing: '1px'
              }}
            >
              ADMINISTRATION
            </Typography>
          )}
        </Box>
        <List>
          {adminItems.map((item, index) => renderMenuItem(item, index))}
        </List>
      </>
    );
  };

  const renderEnterpriseSection = () => {
    if (!hasEnterpriseAdminAccess) return null;

    return (
      <>
        <Divider sx={{ my: 2, mx: 2 }} />
        <Box sx={{ px: 2, py: 1 }}>
          {open && (
            <Typography
              variant="overline"
              sx={{
                color: brandColors.primary,
                fontWeight: 600,
                fontSize: '0.75rem',
                letterSpacing: '1px'
              }}
            >
              ENTERPRISE
            </Typography>
          )}
        </Box>
        <List>
          {enterpriseAdminItems.map((item, index) => renderMenuItem(item, `enterprise-${index}`))}
        </List>
      </>
    );
  };

  const renderFilmAdminSection = () => {
    if (!hasFilmAdminAccess) return null;

    return (
      <>
        <Divider sx={{ my: 2, mx: 2 }} />
        <Box sx={{ px: 2, py: 1 }}>
          {open && (
            <Typography
              variant="overline"
              sx={{
                color: brandColors.secondary,
                fontWeight: 600,
                fontSize: '0.75rem',
                letterSpacing: '1px'
              }}
            >
              FILM ADMIN
            </Typography>
          )}
        </Box>
        <List>
          {filmAdminItems.map((item, index) => renderMenuItem(item, `film-admin-${index}`))}
        </List>
      </>
    );
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={onToggle}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }
      }}
      sx={{
        display: { xs: isMobile ? 'block' : 'none', md: 'block' },
        '& .MuiDrawer-paper': {
          width: isMobile ? drawerWidth : (open ? drawerWidth : collapsedDrawerWidth),
          boxSizing: 'border-box',
          backgroundColor: brandColors.white,
          borderRight: `1px solid ${brandColors.primary}20`,
          transition: isMobile ? 'none' : 'width 0.2s ease-in-out',
          overflowX: 'hidden',
          height: '100vh' // Full height for both mobile and desktop
        }
      }}
    >
      {/* Toggle Button - Only show on desktop */}
      {!isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <Tooltip title={open ? 'Collapse Sidebar' : 'Expand Sidebar'}>
            <IconButton onClick={onToggle} size="small">
              {open ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Divider sx={{ mx: 2 }} />

      {/* Home Section */}
      <List sx={{ pt: 3 }}>
        {menuItems.map((item, index) => renderMenuItem(item, index))}
      </List>

      {/* Film Tracking Section */}
      <Divider sx={{ my: 2, mx: 2 }} />
      <Box sx={{ px: 2, py: 1 }}>
        {open && (
          <Typography
            variant="overline"
            sx={{
              color: brandColors.secondary,
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '1px'
            }}
          >
            FILM TRACKING
          </Typography>
        )}
      </Box>
      <List>
        {filmTrackingItems.map((item, index) => renderMenuItem(item, index))}
      </List>

      {/* Agents Section */}
      {renderAgentsSection()}

      {/* Utilities Section */}
      <Divider sx={{ my: 2, mx: 2 }} />
      <Box sx={{ px: 2, py: 1 }}>
        {open && (
          <Typography
            variant="overline"
            sx={{
              color: brandColors.secondary,
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '1px'
            }}
          >
            UTILITIES
          </Typography>
        )}
      </Box>
      <List>
        {utilityItems.map((item, index) => renderMenuItem(item, index))}
      </List>

      {/* Admin Section */}
      {renderAdminSection()}

      {/* Film Admin Section */}
      {renderFilmAdminSection()}

      {/* Enterprise Admin Section */}
      {renderEnterpriseSection()}

      {/* Footer */}
      <Box sx={{ mt: 'auto', p: 2 }}>
        {open && (
          <Typography
            variant="caption"
            sx={{
              color: brandColors.primary,
              opacity: 0.6,
              textAlign: 'center',
              display: 'block'
            }}
          >
            GA Platform v1.0
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
